import { DataFeedFileType } from '@cbinsights/externalapiservice/externalapiservice';
import { Alert, Button, Checkbox, Form, FormInstance, Spin } from 'antd';
import {
  DataFeedFormField,
  FileTypeMappingByDataset,
  SupportedDataset,
} from './DataFeedForm';
import {
  OptionalColumnsSelector,
  useGetOptionalColumnCatalog,
} from './OptionalColumnsSelector';

export const filesCanContinue = (form: FormInstance) =>
  (form.getFieldValue(DataFeedFormField.files) ?? []).length > 0;

export const DataFeedFileTypeLabels: { [key in DataFeedFileType]: string } = {
  [DataFeedFileType.FILE_TYPE_UNSPECIFIED]:
    'unspecified (this is a test or bug)',
  [DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS]: 'Firmographics CSV',
  [DataFeedFileType.FILE_TYPE_RELATED_ORGS]: 'Firmographics related orgs CSV',
  [DataFeedFileType.FILE_TYPE_COMPETITORS]: 'Firmographics competitors CSV',
  [DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS_LANDSCAPE]:
    'Firmographics landscape CSV',
  [DataFeedFileType.FILE_TYPE_FIRMOGRAPHICS_NAICS_TAXONOMY]:
    'Firmographics NAICS taxonomy CSV',
  [DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIPS]:
    'Business relationships CSV',
  [DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIP_PARTNERS]:
    'Business relationship partners CSV',
  [DataFeedFileType.FILE_TYPE_BUSINESS_RELATIONSHIP_SOURCES]:
    'Business relationship sources CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS]:
    'Financial transactions CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTION_INVESTORS]:
    'Financial transaction investors CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_CAP_TABLE_HISTORY]:
    'Financial transactions cap table history CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_PORTFOLIO_EXITS]:
    'Financial transactions exited investors CSV',
  [DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD]: 'Management and board CSV',
  [DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_EDUCATION]:
    'Management and board education CSV',
  [DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_EXPERIENCE]:
    'Management and board experience CSV',
  [DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_BOARD_ASSOCIATIONS]:
    'Management and board associations CSV',
  [DataFeedFileType.FILE_TYPE_MANAGEMENT_AND_BOARD_LEADERSHIP_SIGNALS]:
    'Management and board leadership signals CSV',
  [DataFeedFileType.FILE_TYPE_COMPANY_OUTLOOK]:
    'Company outlook CSV | Should be used for new customers instead of mosaic 2.1.',
  [DataFeedFileType.FILE_TYPE_MOSAIC_SCORE_HISTORY]: 'Mosaic score history CSV',
  [DataFeedFileType.FILE_TYPE_EXIT_PROBABILITY_HISTORY]:
    'Exit probability history CSV',
  [DataFeedFileType.FILE_TYPE_COMMERCIAL_MATURITY_HISTORY]:
    'Commercial maturity history CSV',
  [DataFeedFileType.UNRECOGNIZED]: 'unrecognized (this is a test or bug)',
  [DataFeedFileType.FILE_TYPE_COMPANY_OUTLOOK_MOSAIC_2_1]:
    'Company outlook mosaic 2.1 CSV | This should only be used for customers that have the 2.1 file in existing feeds.',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_MNA]:
    'Financial transactions M&A CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTION_INVESTORS_MNA]:
    'Financial transaction investors M&A CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_CAP_TABLE_HISTORY_MNA]:
    'Financial transactions cap table history M&A CSV',
  [DataFeedFileType.FILE_TYPE_FINANCIAL_TRANSACTIONS_PORTFOLIO_EXITS_MNA]:
    'Financial transactions exited investors M&A CSV',
  [DataFeedFileType.FILE_TYPE_REVENUE_BY_YEAR]: 'Revenue by year CSV',
  [DataFeedFileType.FILE_TYPE_REVENUE_BY_YEAR_SOURCES]:
    'Revenue by year sources CSV',
  [DataFeedFileType.FILE_TYPE_OUTLOOK_FUNDING_WINDOW]:
    'Outlook funding window CSV',
  [DataFeedFileType.FILE_TYPE_OUTLOOK_FUNDING_WINDOW_COHORT_CRITERIA]:
    'Outlook funding window cohort criteria CSV',
};

const FileTypeSelector = ({
  value,
  onChange,
  dataset,
}: {
  value?: DataFeedFileType[];
  onChange?: (files: DataFeedFileType[]) => void;
  dataset: SupportedDataset;
}) => {
  const selectedFiles = value ?? [];

  const toggleFile = (file: DataFeedFileType) => {
    onChange?.(
      selectedFiles.includes(file)
        ? selectedFiles.filter((f) => f !== file)
        : [...selectedFiles, file]
    );
  };

  return (
    <div>
      {FileTypeMappingByDataset[dataset]?.map((file) => {
        const checked = selectedFiles.includes(file);
        return (
          <div key={file} className="mb-2">
            <Checkbox checked={checked} onChange={() => toggleFile(file)}>
              {DataFeedFileTypeLabels[file]}
            </Checkbox>
            {checked && <OptionalColumnsSelector fileType={file} />}
          </div>
        );
      })}
    </div>
  );
};

export const DataFeedFiles = () => {
  const { isLoading, isSuccess, isFetching, errorUpdatedAt, refetch } =
    useGetOptionalColumnCatalog();
  // Retrying resets status to 'loading', so keep the alert until a fetch succeeds.
  const catalogFailed = !isSuccess && errorUpdatedAt > 0;

  return (
    <Form.Item shouldUpdate>
      {({ getFieldValue }) => {
        const dataset: SupportedDataset = getFieldValue(
          DataFeedFormField.dataset
        );
        return (
          <>
            <Form.Item
              name={[DataFeedFormField.files]}
              label="Select the files to include in the Data Feed"
              required
            >
              <FileTypeSelector dataset={dataset} />
            </Form.Item>
            {isLoading && !catalogFailed && <Spin />}
            {catalogFailed && (
              <Alert
                type="error"
                className="mb-4"
                message="Could not load optional columns"
                description="Retry to continue."
                showIcon
                action={
                  <Button
                    size="small"
                    loading={isFetching}
                    onClick={() => refetch()}
                  >
                    Retry
                  </Button>
                }
              />
            )}
            <Form.Item
              name={[DataFeedFormField.deltas]}
              valuePropName="checked"
              noStyle
            >
              <Checkbox>Include delta files</Checkbox>
            </Form.Item>
          </>
        );
      }}
    </Form.Item>
  );
};
